import { useState, useEffect, useCallback } from 'react'
import { Plus, Key, Trash2, Copy, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { apiClient } from '@/lib/api-client'
import { CreateApiKeyDialog } from './CreateApiKeyDialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { format } from 'date-fns'

interface ApiKey {
  id: string
  name: string
  key_prefix: string
  key?: string
  permissions: string[]
  last_used_at: string | null
  expires_at: string | null
  created_at: string
}

export function ApiKeyList() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null)
  const [createdKey, setCreatedKey] = useState<{ name: string; value: string } | null>(null)
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle')
  const [statusMessage, setStatusMessage] = useState<
    | {
        variant: 'success' | 'error'
        title: string
        description?: string
      }
    | null
  >(null)

  const fetchApiKeys = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await apiClient.getApiKeys()
      if (response.success && response.data) {
        setApiKeys(response.data as ApiKey[])
      } else {
        throw new Error(response.error || 'Failed to fetch API keys')
      }
      setStatusMessage(null)
    } catch (error) {
      setStatusMessage({
        variant: 'error',
        title: 'Failed to load API keys',
        description: error instanceof Error ? error.message : 'Please try again.'
      })
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchApiKeys()
  }, [fetchApiKeys])

  const handleKeyCreated = (newKey: ApiKey & { key: string }) => {
    setApiKeys(prev => [newKey, ...prev])
    setIsCreateDialogOpen(false)
    setCreatedKey({ name: newKey.name, value: newKey.key })
    setCopyFeedback('idle')
    setStatusMessage(null)
  }

  const handleDeleteKey = async () => {
    if (!keyToDelete) return

    try {
      const response = await apiClient.revokeApiKey(keyToDelete)
      if (response.success) {
        setApiKeys(prev => prev.filter(key => key.id !== keyToDelete))
        setStatusMessage({
          variant: 'success',
          title: 'API key revoked',
          description: 'The key was removed and workflows using it will stop immediately.'
        })
      } else {
        throw new Error(response.error || 'Failed to revoke API key')
      }
    } catch (error) {
      setStatusMessage({
        variant: 'error',
        title: 'Failed to revoke API key',
        description: error instanceof Error ? error.message : 'Please retry in a moment.'
      })
    } finally {
      setKeyToDelete(null)
    }
  }

  const handleCopyKey = useCallback(async () => {
    if (!createdKey) return

    try {
      if ('clipboard' in navigator && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(createdKey.value)
        setCopyFeedback('copied')
      } else {
        throw new Error('Clipboard API unavailable')
      }
    } catch {
      setCopyFeedback('error')
    }
  }, [createdKey])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-muted-foreground">Loading API keys...</div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          Use API keys to authenticate with the EtherPilot API from GitHub Actions or your CI/CD pipeline.
        </p>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create API Key
        </Button>
      </div>

      {createdKey && (
        <div className="rounded-lg border bg-muted/40 p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-semibold">Save this API key now</h3>
              <p className="text-sm text-muted-foreground">
                This key is only shown once. Copy it and store it in your secret manager.
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCreatedKey(null)}
              aria-label="Dismiss new API key"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <code className="break-all rounded bg-background px-3 py-2 text-sm shadow-sm border">
              {createdKey.value}
            </code>
            <Button variant="outline" size="sm" onClick={handleCopyKey} className="flex items-center gap-2">
              <Copy className="h-4 w-4" />
              Copy
            </Button>
            {copyFeedback === 'copied' && (
              <span className="text-sm text-emerald-600">Copied to clipboard</span>
            )}
            {copyFeedback === 'error' && (
              <span className="text-sm text-destructive">Clipboard unavailable — copy manually</span>
            )}
          </div>
        </div>
      )}

      {statusMessage && (
        <Alert
          variant={statusMessage.variant === 'error' ? 'destructive' : 'default'}
          className={
            statusMessage.variant === 'error'
              ? 'bg-destructive/10'
              : 'bg-muted'
          }
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <AlertTitle>{statusMessage.title}</AlertTitle>
              {statusMessage.description && (
                <AlertDescription>{statusMessage.description}</AlertDescription>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setStatusMessage(null)}
              aria-label="Dismiss message"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </Alert>
      )}

      {apiKeys.length === 0 ? (
        <div className="border rounded-lg p-8 text-center">
          <Key className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="font-semibold mb-2">No API Keys</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Create your first API key to start using EtherPilot in your CI/CD pipeline
          </p>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Your First API Key
          </Button>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Key</TableHead>
              <TableHead>Last Used</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {apiKeys.map((apiKey) => (
              <TableRow key={apiKey.id}>
                <TableCell className="font-medium">{apiKey.name}</TableCell>
                <TableCell>
                  <code className="text-xs bg-muted px-2 py-1 rounded">
                    {apiKey.key_prefix}...
                  </code>
                </TableCell>
                <TableCell>
                  {apiKey.last_used_at
                    ? format(new Date(apiKey.last_used_at), 'MMM d, yyyy')
                    : 'Never'
                  }
                </TableCell>
                <TableCell>
                  {apiKey.expires_at
                    ? format(new Date(apiKey.expires_at), 'MMM d, yyyy')
                    : 'Never'
                  }
                </TableCell>
                <TableCell>
                  {format(new Date(apiKey.created_at), 'MMM d, yyyy')}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setKeyToDelete(apiKey.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <CreateApiKeyDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onKeyCreated={handleKeyCreated}
      />

      <AlertDialog open={!!keyToDelete} onOpenChange={() => setKeyToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API Key</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Any applications or workflows using this key will stop working immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteKey} className="bg-destructive text-destructive-foreground">
              Revoke Key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
